import { camelCase } from 'lodash';
import React from 'react';

import { DRUG_TYPES, SERVICE_TYPES } from 'Containers/ResultPage/constants';
import { copayAsRange } from 'Containers/ViewPlanPage/_shared';
import { useFeatureFlagContext } from 'Contexts/featureFlagContext';
import { useTextContext } from 'Contexts/textContext';
import { H6, Numbers, Paragraph } from 'DesignLibrary/atoms';
import Plan from 'Models/plan';
import Text from 'Shared/Text';
import { HealthCoverage } from 'Types/entities';
import { toDollars } from 'Utils/helpers';

import { CopayTag } from '../styled';

export interface CopayInfoProps {
  plan: Plan;
  copayType: DRUG_TYPES | SERVICE_TYPES;
  perVisit?: boolean;
  coverageOverride?: HealthCoverage | null;
  /*
  tier is an optional argument that only gets passed when client is Vidant Health.
  because each copayType has an array of objects (multiple tiers) when the client is Vidant Health
  we are passing the specific tier that is getting rendered
  */
  tier?: {
    underline: string;
    copay: number;
    coinsurance: number;
    titleRef: string;
    textRef: string;
    copayText?: string;
    coinsuranceText?: string;
  };
}

const CopayInfo = ({ plan, copayType, perVisit = false, tier, coverageOverride }: CopayInfoProps) => {
  const { retrieveContentfulData } = useTextContext();
  const noOonCoverageText =
    retrieveContentfulData<string>('health_section.compare_plan_text.compareModal.noOonCoverage') ||
    'No out-of-network coverage';

  if (coverageOverride === null) {
    return <Paragraph size="small">{noOonCoverageText}</Paragraph>;
  }

  const current = coverageOverride ?? plan[copayType];
  const isCopay = current.copay != null;
  const isCovered = current.covered;
  const deductibleApplies = current.deductible_applies;

  if (!isCovered) {
    return (
      <Paragraph className="not-covered">
        <Text field="health_section.compare_plan_text.compareModal.notCovered" />
      </Paragraph>
    );
  }
  const { is_no_deductible_plans_enabled } = useFeatureFlagContext();
  const isCopayAsRange = (copayType: string): boolean => {
    return (
      is_no_deductible_plans_enabled &&
      plan.isNoDeductiblePlan &&
      ['specialistCare', 'primaryCare'].includes(copayType) &&
      current.copay_min != null &&
      current.copay_max != null
    );
  };

  return (
    <div
      aria-labelledby={
        deductibleApplies ? `${copayType}${plan.planId}${camelCase(tier?.underline)}CopayCoinsuranceCell` : ''
      }
      aria-describedby={deductibleApplies ? 'afterDeductibleDisclaimer' : ''}
    >
      <div
        aria-hidden={!!deductibleApplies}
        className={isCopay ? 'copay' : 'coinsurance'}
        id={`${copayType}${plan.planId}${camelCase(tier?.underline)}CopayCoinsuranceCell`}
      >
        <div className="inline-numbers">
          <Numbers size="medium">
            {isCopayAsRange(copayType)
              ? copayAsRange(current.copay_min, current.copay_max, true)
              : isCopay
                ? toDollars(tier ? tier.copay : current.copay)
                : `${tier ? tier.coinsurance : current.coinsurance}%`}
          </Numbers>
          {plan[copayType].max_cost_sharing && (
            <Paragraph size="small">
              <Text
                vars={{
                  x: toDollars(plan[copayType].max_cost_sharing),
                }}
                field="health_section.min_max_cost_sharing.maxCostSharing"
              />
            </Paragraph>
          )}
          {isCopay && perVisit && (
            <Paragraph size="small" color="--text-gray-light">
              <Text field="health_section.compare_plan_text.compareModal.perVisit" />
            </Paragraph>
          )}
          {is_no_deductible_plans_enabled && isCopayAsRange(copayType) ? '' : deductibleApplies && '*'}
        </div>
        <CopayTag>
          <H6 as="span">
            {isCopay ? (
              <Text field="health_section.compare_plan_text.compareModal.copay" />
            ) : (
              <Text field="health_section.compare_plan_text.compareModal.coinsurance" />
            )}
          </H6>
        </CopayTag>
      </div>
    </div>
  );
};

export default CopayInfo;
